import { useCallback, useEffect, useMemo, useState } from 'react'
import { DRAW_BET_TYPES, DRAW_BET_TYPE_DIGITS } from '@nexbit/types'
import type { DrawLotteryWithCurrent, DrawBetType, DrawBlockedNumber } from '@nexbit/types'
import { ChoiceSelect } from '@/components/choice-select'
import { betChoices } from '@/lib/bet-choices'
import {
  apiDrawGetLotteries,
  apiDrawExposure,
  apiDrawUpdateLimits,
  apiDrawBlockedNumbers,
  apiDrawBlockNumber,
  apiDrawUnblockNumber,
  ApiRequestError,
  isOtpRequiredError,
  type DrawExposure,
} from '@/lib/api'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Ban, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import { useCan } from '@/hooks/usePermission'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import NumberCapsPanel from './number-caps-panel'
import { useNumberCaps } from './use-number-caps'

/**
 * Which lottery the page lands on before the admin has picked one themselves.
 *
 * หวยรัฐบาลไทย is the one an admin most often opens this page for, so it wins outright
 * regardless of whether it happens to be open — the old rule (whichever lottery is
 * currently open, else the first in the list) put the admin on a different lottery
 * every time depending on the time of day, since หวยรัฐบาลไทย, หวยออมสิน and หวย ธ.ก.ส.
 * are commonly open together for days at a stretch.
 */
export function defaultLotteryId(lotteries: DrawLotteryWithCurrent[]): string | null {
  const government = lotteries.find((l) => l.code === 'government')
  if (government) return government.id
  return (lotteries.find((l) => l.currentDraw?.status === 'open') ?? lotteries[0])?.id ?? null
}

const BET_TYPES: readonly DrawBetType[] = DRAW_BET_TYPES
/** Digits a number must have per bet type; 0 = the odd/even & high/low choices. */
const BET_TYPE_DIGITS = DRAW_BET_TYPE_DIGITS

/**
 * Whether a block-box value is a real number for this bet type.
 *
 * Checked against THIS type's own choices. It used to accept any of odd/even/high/low for
 * any choice type, so 'high' left over from สูง/ต่ำ passed as valid on คู่/คี่ — and the
 * server, which does know the difference, refused it.
 */
function isBlockValueFor(betType: DrawBetType, value: string): boolean {
  const choices = betChoices(betType)
  if (choices) return choices.some((c) => c.value === value.trim().toLowerCase())
  return new RegExp(`^\\d{${BET_TYPE_DIGITS[betType]}}$`).test(value)
}

const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const REFRESH_MS = 5000

/** A number-input row for one cap. */
function CapField({
  label, hint, value, onChange, invalid,
}: {
  label: string
  hint: string
  value: string
  onChange: (v: string) => void
  invalid: boolean
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </Label>
      <Input
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ''))}
        aria-invalid={invalid}
        className={cn('h-9 w-full font-mono text-sm tabular-nums', invalid && 'border-destructive')}
      />
      <p className="font-mono text-[10px] text-muted-foreground">{hint}</p>
    </div>
  )
}

/**
 * Everything about what a lottery will accept, in one place: the caps for a bet
 * type, the stake each number of that type is carrying against them, and the block
 * list.
 *
 * Organised around picking a lottery and a bet type, because that is the unit an
 * operator thinks in — "หวยมาเลย์ 3 ตัวบน, what am I holding and where is the ceiling".
 * The table answers that directly and each row can be closed from where it is read.
 */
export default function NumberLimitsPage() {
  const t = useT()
  const can = useCan()

  const [lotteries, setLotteries] = useState<DrawLotteryWithCurrent[]>([])
  const [lotteryId, setLotteryId] = useState<string | null>(null)
  const [betType, setBetType] = useState<DrawBetType>('3top')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [exposure, setExposure] = useState<DrawExposure | null>(null)
  const [blocked, setBlocked] = useState<DrawBlockedNumber[]>([])
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // Cap editor — held as strings so a half-typed value does not fight the input.
  const [caps, setCaps] = useState({ maxBet: '', maxPerNumber: '', maxPerType: '' })

  // Manual block entry
  const [blockNum, setBlockNum] = useState('')
  const [wholeLottery, setWholeLottery] = useState(false)

  // The house's per-number ceilings. Loaded here, not inside the panel that edits them,
  // because the stake table below has to measure each number against its OWN ceiling.
  const numberCaps = useNumberCaps(lotteryId)

  const lottery = lotteries.find((l) => l.id === lotteryId) ?? null
  const liveDraw = lottery?.currentDraw?.status === 'open' ? lottery.currentDraw : null
  const cfg = lottery?.betConfig[betType]

  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const data = await apiDrawGetLotteries()
        if (!alive) return
        setLotteries(data)
        setLotteryId((prev) => prev ?? defaultLotteryId(data))
      } catch (e) {
        if (alive) setError(e instanceof ApiRequestError ? e.message : t.common.error)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [t])

  // Load the caps of whichever (lottery, bet type) is in view.
  useEffect(() => {
    if (!cfg) return
    setCaps({
      maxBet: String(cfg.maxBet),
      maxPerNumber: String(cfg.maxPerNumber),
      maxPerType: String(cfg.maxPerType ?? 0),
    })
  }, [cfg])

  // Keyed on ids, not the objects: `lottery` is a fresh reference on every render, so
  // depending on it would rebuild refresh each time, restart the interval, and let
  // overlapping responses land out of order.
  const liveDrawId = liveDraw?.id ?? null

  const refresh = useCallback(async () => {
    if (!lotteryId) return
    try {
      const [exp, blk] = await Promise.all([
        liveDrawId ? apiDrawExposure(liveDrawId, betType) : Promise.resolve(null),
        apiDrawBlockedNumbers(lotteryId, liveDrawId ?? undefined),
      ])
      setExposure(exp)
      setBlocked(blk)
      setActionError(null)
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    }
  }, [lotteryId, liveDrawId, betType, t])

  // Poll: the operator watches a figure creep up, not sub-second events.
  useEffect(() => {
    void refresh()
    const id = setInterval(() => void refresh(), REFRESH_MS)
    return () => clearInterval(id)
  }, [refresh])

  const blockedSet = useMemo(
    () => new Set(blocked.map((b) => `${b.betType}|${b.number}`)),
    [blocked],
  )

  async function reloadLotteries() {
    try {
      setLotteries(await apiDrawGetLotteries())
    } catch { /* the polling refresh will surface any real problem */ }
  }

  const capsNum = {
    maxBet: Number(caps.maxBet || 0),
    maxPerNumber: Number(caps.maxPerNumber || 0),
    maxPerType: Number(caps.maxPerType || 0),
  }
  const capsInvalid =
    capsNum.maxBet <= 0 ||
    capsNum.maxPerNumber < capsNum.maxBet ||
    capsNum.maxPerType < capsNum.maxPerNumber
  const capsDirty = !!cfg && (
    capsNum.maxBet !== cfg.maxBet ||
    capsNum.maxPerNumber !== cfg.maxPerNumber ||
    capsNum.maxPerType !== (cfg.maxPerType ?? 0)
  )

  // Raising any of the three ceilings waits here for a code from the Telegram group.
  // Lowering them never does: it only restricts what the house takes, and it is the lever
  // an operator pulls mid-draw when a type runs heavy.
  const [pendingLimits, setPendingLimits] = useState<
    { betType: DrawBetType; maxBet: number; maxPerNumber: number; maxPerType: number } | null
  >(null)

  async function afterLimitsSaved() {
    await reloadLotteries()
    await refresh()
    setSavedAt(Date.now())
  }

  async function saveCaps() {
    if (!lottery || !cfg) return
    setActionError(null)
    const body = { betType, ...capsNum }

    // Worked out here so the code is asked for up front. The server decides again against
    // what it holds, and if the two disagree the refusal below opens the same dialog.
    const raises = capsNum.maxBet > cfg.maxBet
      || capsNum.maxPerNumber > cfg.maxPerNumber
      || capsNum.maxPerType > (cfg.maxPerType ?? 0)
    if (raises) { setPendingLimits(body); return }

    setBusy(true)
    try {
      await apiDrawUpdateLimits(lottery.id, body)
      await afterLimitsSaved()
    } catch (e) {
      if (isOtpRequiredError(e)) setPendingLimits(body)
      else setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setBusy(false)
    }
  }

  async function block(num: string, allDraws: boolean) {
    if (!lottery) return
    setBusy(true)
    setActionError(null)
    try {
      await apiDrawBlockNumber(lottery.id, {
        betType,
        number: num.trim().toLowerCase(),
        drawId: allDraws ? null : (liveDraw?.id ?? null),
      })
      setBlockNum('')
      await refresh()
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setBusy(false)
    }
  }

  async function unblock(id: string) {
    setBusy(true)
    try {
      await apiDrawUnblockNumber(id)
      await refresh()
    } catch (e) {
      setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setBusy(false)
    }
  }

  const digits = BET_TYPE_DIGITS[betType]
  const choices = betChoices(betType)
  const blockValid = isBlockValueFor(betType, blockNum)

  // A number typed or picked for one bet type means nothing on another: '45' on a choice
  // type, or 'high' left over on คู่/คี่. A select holding such a value shows blank while
  // the add button stays live, so it is dropped on the switch. Only when INVALID for the
  // new type — a 3-digit number is still fine on 3tote.
  useEffect(() => {
    setBlockNum((cur) => (cur !== '' && !isBlockValueFor(betType, cur) ? '' : cur))
  }, [betType])

  // Rows for the selected type only — the exposure call is already narrowed to it.
  const rows = (exposure?.byNumber ?? []).filter((n) => n.betType === betType)
  const typeTaken = Number(exposure?.byType.find((x) => x.betType === betType)?.stake ?? 0)
  const typeBets = exposure?.byType.find((x) => x.betType === betType)?.bets ?? 0
  const typeCap = cfg?.maxPerType ?? 0
  const typePct = typeCap > 0 ? Math.min(100, Math.round((typeTaken / typeCap) * 100)) : 0

  // What each number would cost if it came up: stake × payout.
  const payout = cfg?.payout ?? 0
  // Only one number can win, so the exposure that matters is the worst single one —
  // not the sum. Netted against everything taken on the type, since that stake is
  // already in hand whichever number lands.
  const worstLiability = rows.reduce((max, n) => Math.max(max, Number(n.stake) * payout), 0)
  const worstNet = worstLiability - typeTaken

  return (
    <div className="p-4 md:p-6 max-w-[1400px]">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.nav.numberLimits}
        </span>
        <p className="mt-1 font-mono text-[11px] text-muted-foreground">{t.draws.blockPageDesc}</p>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-card p-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-80" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <>
          {/* ── Scope: lottery + bet type ───────────────────────────────── */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <select
              value={lotteryId ?? ''}
              onChange={(e) => { setLotteryId(e.target.value); setExposure(null) }}
              className="h-9 rounded-[var(--radius-sm)] border border-input bg-background px-2 font-mono text-sm text-foreground"
              aria-label={t.draws.lotteries}
            >
              {lotteries.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}{l.currentDraw?.status === 'open' ? ' ●' : ''}
                </option>
              ))}
            </select>

            <div className="flex flex-wrap gap-1">
              {BET_TYPES.filter((k) => lottery?.betConfig[k]?.enabled).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => { setBetType(k); setExposure(null) }}
                  aria-pressed={betType === k}
                  className={cn(
                    'h-9 rounded-[var(--radius-sm)] border px-2.5 font-mono text-xs transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    betType === k
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground hover:bg-muted',
                  )}
                >
                  {t.draws.betTypeLabel[k]}
                </button>
              ))}
            </div>

            {liveDraw ? (
              <span className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" aria-hidden />
                {t.draws.drawNumber} {liveDraw.drawNumber} · {t.draws.exposureAuto}
              </span>
            ) : (
              <span className="font-mono text-[11px] text-muted-foreground">
                {t.draws.blockNoLiveDraw}
              </span>
            )}
          </div>

          {actionError && (
            <p className="mb-3 font-mono text-[11px] text-destructive">{actionError}</p>
          )}

          <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
            {/* ── Caps for this bet type ──────────────────────────────── */}
            <div className="self-start rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
              <div className="border-b border-border bg-secondary px-4 py-2.5">
                <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                  {t.draws.limitsTitle} — {t.draws.betTypeLabel[betType]}
                </span>
              </div>
              <div className="space-y-3 p-4">
                {!cfg ? (
                  <p className="font-mono text-[11px] text-muted-foreground">{t.draws.limitsNoConfig}</p>
                ) : (
                  <>
                    <CapField
                      label={t.draws.payoutMax}
                      hint={t.draws.limitsMaxBetHint}
                      value={caps.maxBet}
                      onChange={(v) => setCaps((c) => ({ ...c, maxBet: v }))}
                      invalid={capsNum.maxBet <= 0}
                    />
                    <CapField
                      label={t.draws.payoutPerNumber}
                      hint={t.draws.limitsPerNumberHint}
                      value={caps.maxPerNumber}
                      onChange={(v) => setCaps((c) => ({ ...c, maxPerNumber: v }))}
                      invalid={capsNum.maxPerNumber < capsNum.maxBet}
                    />
                    <CapField
                      label={t.draws.payoutPerType}
                      hint={t.draws.limitsPerTypeHint}
                      value={caps.maxPerType}
                      onChange={(v) => setCaps((c) => ({ ...c, maxPerType: v }))}
                      invalid={capsNum.maxPerType < capsNum.maxPerNumber}
                    />
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {savedAt && !capsDirty ? t.draws.limitsSaved : ''}
                      </span>
                      {can('number-limits:edit') && (
                      <Button
                        size="sm"
                        className="h-8 gap-1.5 font-mono text-xs"
                        disabled={!capsDirty || capsInvalid || busy}
                        onClick={() => void saveCaps()}
                      >
                        <Check className="h-3 w-3" />
                        {t.common.save}
                      </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Numbers held on this bet type ───────────────────────── */}
            <div className="rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)] overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-secondary px-4 py-2.5">
                <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                  {t.draws.exposureTitle} — {t.draws.betTypeLabel[betType]}
                </span>
                <span
                  className={cn(
                    'font-mono text-xs tabular-nums',
                    typePct >= 100 ? 'font-semibold text-destructive' : 'text-secondary-foreground/80',
                  )}
                >
                  {nf.format(typeTaken)} / {nf.format(typeCap)} · {typePct}% · {typeBets} {t.draws.exposureBets}
                </span>
              </div>

              {/* Type-level fill, so the ceiling is visible without reading every row. */}
              <div className="px-4 pt-3">
                <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      typePct >= 100 ? 'bg-destructive' : typePct >= 80 ? 'bg-warning' : 'bg-primary',
                    )}
                    style={{ width: `${typePct}%` }}
                  />
                </div>
              </div>

              {/* Worst case for this type. Only one number can come up, so this is the
                  heaviest single number, not the sum — netted against the stake
                  already taken, which the house keeps whichever number lands. */}
              {rows.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 pt-3">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {t.draws.limitsWorstCase}{' '}
                    <span className="font-semibold tabular-nums text-destructive">
                      {nf.format(worstLiability)}
                    </span>
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {t.draws.limitsWorstNet}{' '}
                    <span
                      className={cn(
                        'font-semibold tabular-nums',
                        worstNet > 0 ? 'text-destructive' : 'text-success',
                      )}
                    >
                      {worstNet > 0 ? '-' : '+'}{nf.format(Math.abs(worstNet))}
                    </span>
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground/70">
                    {t.draws.limitsWorstHint}
                  </span>
                </div>
              )}

              {!liveDraw ? (
                <p className="p-4 font-mono text-[11px] text-muted-foreground">
                  {t.draws.blockNoLiveDraw}
                </p>
              ) : exposure === null ? (
                <div className="space-y-2 p-4">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ) : rows.length === 0 ? (
                <p className="p-4 font-mono text-[11px] text-muted-foreground">
                  {t.draws.limitsNoBets}
                </p>
              ) : (
                <div className="max-h-[52vh] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card">
                      <TableRow>
                        <TableHead>{t.draws.limitsNumber}</TableHead>
                        <TableHead className="text-right">{t.draws.exposureTaken}</TableHead>
                        <TableHead className="text-right">{t.draws.limitsLiability}</TableHead>
                        <TableHead className="text-right">{t.draws.payoutPerNumber}</TableHead>
                        <TableHead>{t.draws.limitsFill}</TableHead>
                        <TableHead className="text-right">{t.draws.exposureBets}</TableHead>
                        <TableHead className="text-right">{t.common.actions}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((n) => {
                        // This number's ceiling: its own override if the house gave it one,
                        // else the bet type's default. Reading only the default showed a number
                        // raised to 20,000 as 100% full at 5,000.
                        const defaultCap = cfg?.maxPerNumber ?? 0
                        const overridden = numberCaps.index.get(`${betType}|${n.number}`)
                        const cap = overridden ?? defaultCap
                        const pct = cap > 0 ? Math.min(100, Math.round((Number(n.stake) / cap) * 100)) : 0
                        const isBlocked = blockedSet.has(`${betType}|${n.number}`)
                        return (
                          <TableRow key={n.number} className={cn(isBlocked && 'bg-destructive/5')}>
                            <TableCell
                              className={cn(
                                'font-mono text-sm font-semibold tracking-widest',
                                isBlocked && 'text-destructive line-through',
                              )}
                            >
                              {n.number}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs tabular-nums text-foreground">
                              {nf.format(Number(n.stake))}
                            </TableCell>
                            {/* What this number costs if it wins — the figure the
                                block decision is actually weighed against. */}
                            <TableCell
                              className={cn(
                                'text-right font-mono text-xs tabular-nums',
                                Number(n.stake) * payout >= worstLiability && worstLiability > 0
                                  ? 'font-semibold text-destructive'
                                  : 'text-warning',
                              )}
                            >
                              {nf.format(Number(n.stake) * payout)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                              {nf.format(cap)}
                              {overridden !== undefined && (
                                <div className="text-[10px] text-muted-foreground/80">
                                  {t.numberCaps.typeDefault(nf.format(defaultCap))}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="w-40">
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                                  <div
                                    className={cn(
                                      'h-full rounded-full',
                                      pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary',
                                    )}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span
                                  className={cn(
                                    'w-9 shrink-0 text-right font-mono text-[10px] tabular-nums',
                                    pct >= 100 ? 'font-semibold text-destructive' : 'text-muted-foreground',
                                  )}
                                >
                                  {pct}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                              {n.bets}
                            </TableCell>
                            <TableCell className="text-right">
                              {isBlocked ? (
                                <span className="font-mono text-[10px] uppercase tracking-wide text-destructive">
                                  {t.draws.limitsClosed}
                                </span>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 gap-1.5 font-mono text-xs"
                                  disabled={busy}
                                  onClick={() => void block(n.number, false)}
                                >
                                  <Ban className="h-3 w-3" />
                                  {t.draws.blockAdd}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>

          {pendingLimits && lottery && (
            <OtpConfirmDialog
              purpose="draw.limits.raise"
              // Exactly what the request will carry, so the code issued for it verifies.
              payload={{ params: { lotteryId: lottery.id }, body: { ...pendingLimits } }}
              title={t.numberCaps.otpTitle}
              description={t.numberCaps.otpLimitsDesc(
                t.draws.betTypeLabel[pendingLimits.betType],
                `${nf.format(pendingLimits.maxBet)} / ${nf.format(pendingLimits.maxPerNumber)} / ${nf.format(pendingLimits.maxPerType)}`,
              )}
              confirmLabel={t.numberCaps.otpConfirm}
              onConfirm={async (otp) => { await apiDrawUpdateLimits(lottery.id, pendingLimits, otp) }}
              onDone={() => { setPendingLimits(null); void afterLimitsSaved() }}
              onClose={() => setPendingLimits(null)}
            />
          )}

          {/* ── Per-number ceilings — exceptions to the default above ─────── */}
          {lottery && (
            <NumberCapsPanel
              lottery={lottery}
              caps={numberCaps.caps}
              state={numberCaps.state}
              onChanged={numberCaps.reload}
              betType={betType}
              onSelectBetType={(bt) => { setBetType(bt); setExposure(null) }}
              liveDrawId={liveDrawId}
              canEdit={can('number-limits:edit')}
            />
          )}

          {/* ── Block list ─────────────────────────────────────────────── */}
          <div className="mt-4 rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
            <div className="border-b border-border bg-secondary px-4 py-2.5">
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                {t.draws.blockTitle}
              </span>
            </div>
            <div className="space-y-3 p-4">
              <p className="font-mono text-[11px] text-muted-foreground">{t.draws.blockHint}</p>

              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-mono text-xs text-muted-foreground">
                  {t.draws.betTypeLabel[betType]}
                </span>
                {choices ? (
                  // One answer at a time here — a block is a single number — so no ทั้งสอง.
                  <ChoiceSelect
                    choices={choices}
                    value={blockNum}
                    onChange={setBlockNum}
                    aria-label={t.draws.limitsNumber}
                    className="h-8 w-32 text-xs"
                  />
                ) : (
                  <Input
                    value={blockNum}
                    onChange={(e) => setBlockNum(e.target.value.replace(/\D/g, '').slice(0, digits))}
                    placeholder={'1'.repeat(digits)}
                    aria-invalid={blockNum.length > 0 && !blockValid}
                    aria-label={t.draws.limitsNumber}
                    className="h-8 w-28 font-mono text-xs tracking-widest"
                  />
                )}
                <label className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={wholeLottery}
                    onChange={(e) => setWholeLottery(e.target.checked)}
                    className="h-3 w-3 accent-[var(--primary)]"
                  />
                  {t.draws.blockAllDraws}
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 font-mono text-xs"
                  disabled={!blockValid || busy || !lottery || (!wholeLottery && !liveDraw)}
                  onClick={() => void block(blockNum, wholeLottery)}
                >
                  <Ban className="h-3 w-3" />
                  {t.draws.blockAdd}
                </Button>
              </div>

              {blocked.length === 0 ? (
                <p className="font-mono text-[11px] text-muted-foreground">{t.draws.blockEmpty}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {blocked.map((b) => (
                    <span
                      key={b.id}
                      className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-destructive/40 bg-destructive/10 px-2 py-1 font-mono text-[11px]"
                    >
                      <span className="text-muted-foreground">{t.draws.betTypeLabel[b.betType]}</span>
                      <span className="font-semibold text-foreground">{b.number}</span>
                      {b.drawId === null && (
                        <span className="text-[9px] uppercase tracking-wide text-destructive">
                          {t.draws.blockAllBadge}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => void unblock(b.id)}
                        disabled={busy}
                        aria-label={`${t.draws.blockRemove} ${b.number}`}
                        className="rounded-[var(--radius-sm)] text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
